import {
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type FC,
    type ReactNode,
} from 'react';
import { createBrowserHistory, type History, type LocationState, type Path } from 'history';

type BrowserHistoryContextValue = {
    back: () => void;
    pushState: (state: LocationState, location: Path) => void;
    currentState: LocationState;
};

export const BrowserHistoryContext = createContext<BrowserHistoryContextValue>({
    back: () => {},
    pushState: () => {},
    currentState: {},
});

type Props = {
    children?: ReactNode;
};

export const BrowserHistoryContextProvider: FC<Props> = ({ children }) => {
    const [currentState, setCurrentState] = useState<LocationState>({});
    const historyRef = useRef<History | null>(null);

    useEffect(() => {
        const history = createBrowserHistory();
        historyRef.current = history;
        const unlisten = history.listen(location => {
            setCurrentState(location.state);
        });

        if (
            window.location.hash === '#zoomModalOpen' ||
            window.location.hash === '#galleryModalOpen'
        ) {
            history.replace('#', null);
        }

        return unlisten;
    }, []);

    const pushState = useCallback((state: LocationState, location: Path): void => {
        if (historyRef.current) {
            historyRef.current.push(location, state);
            setCurrentState(state);
        }
    }, []);

    const back = useCallback((): void => {
        historyRef.current?.goBack();
    }, []);

    const value = useMemo(
        () => ({
            back,
            pushState,
            currentState: currentState || {},
        }),
        [back, pushState, currentState]
    );

    return <BrowserHistoryContext value={value}>{children}</BrowserHistoryContext>;
};
